import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { Sparkles, Send, RotateCcw, Loader2, Lock, Bot, FileText } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { needsUpgrade, openUpgrade } from "@/lib/planFeatures";
import api from "@/lib/api";

export default function Advisor() {
  const { company } = useAuth();
  const [messages, setMessages] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [drafting, setDrafting] = useState(null);
  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const locked = company && needsUpgrade(company.plan, "growth");

  const load = async () => {
    if (!company) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/companies/${company.id}/advisor/thread`, { quiet402: true });
      setMessages(data.messages || []);
      setSuggestions(data.suggestions || []);
    } catch { /* locked */ }
    finally { setLoading(false); }
  };
  useEffect(() => { if (locked) setLoading(false); else load(); /* eslint-disable-next-line */ }, [locked, company]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, sending]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }]);
    setSending(true);
    try {
      const { data } = await api.post(`/companies/${company.id}/advisor/ask`, { question: q });
      setMessages((m) => [...m, { role: "assistant", content: data.answer }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ask TenX is unavailable right now");
      setMessages((m) => m.slice(0, -1));
      setInput(q);
    } finally { setSending(false); }
  };

  const reset = async () => {
    try { await api.post(`/companies/${company.id}/advisor/reset`); setMessages([]); toast.success("Conversation cleared"); }
    catch { toast.error("Could not clear"); }
  };

  const draftUpdate = async (content, idx) => {
    setDrafting(idx);
    try {
      const { data } = await api.post(`/companies/${company.id}/advisor/draft-update`, { content });
      toast.success("Draft update created from this answer");
      navigate(`/app/updates/${data.update_id}/edit`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create a draft"); }
    finally { setDrafting(null); }
  };

  if (locked) {
    return (
      <AppShell title="Ask TenX" subtitle="Your AI startup advisor">
        <Card className="mx-auto mt-10 max-w-lg rounded-xl border p-8 text-center" data-testid="advisor-upgrade">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary"><Lock className="h-6 w-6 text-primary-foreground" /></div>
          <h2 className="ff-cabinet mt-4 text-xl font-bold text-foreground">Meet your AI advisor</h2>
          <p className="mt-2 text-sm text-muted-foreground">Ask TenX answers fundraising, cap table, governance and investor-relations questions - grounded in your own company data. Available on Growth and above.</p>
          <Button className="mt-5" onClick={() => openUpgrade({ plan: "growth" })} data-testid="advisor-upgrade-btn">Upgrade to unlock</Button>
        </Card>
      </AppShell>
    );
  }

  const resetAction = messages.length > 0 ? (
    <Button variant="outline" size="sm" className="gap-1.5" onClick={reset} data-testid="advisor-reset"><RotateCcw className="h-3.5 w-3.5" /> Clear</Button>
  ) : null;

  return (
    <AppShell title="Ask TenX" subtitle="Your AI startup advisor, grounded in your company data" actions={resetAction}>
      <div className="mx-auto flex h-[calc(100vh-9rem)] max-w-3xl flex-col">
        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto pb-4" data-testid="advisor-messages">
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary"><Sparkles className="h-7 w-7 text-brand" /></div>
              <h2 className="ff-cabinet mt-4 text-xl font-bold text-foreground">How can I help you build?</h2>
              <p className="mt-1 max-w-md text-sm text-muted-foreground">Fundraising strategy, cap table math, investor updates, governance - ask anything. I'll use your live TenX data where it helps.</p>
              <div className="mt-6 grid w-full max-w-lg gap-2">
                {suggestions.map((s) => (
                    <button key={s} onClick={() => send(s)} className="rounded-lg border bg-card px-4 py-2.5 text-left text-sm font-medium text-foreground transition-colors hover:border-brand/50 hover:bg-secondary/50" data-testid={`advisor-suggestion-${suggestions.indexOf(s)}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, i) => (
              <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`advisor-msg-${m.role}`}>
                  {m.role === "assistant" && <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary"><Bot className="h-4 w-4 text-brand" /></div>}
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "bg-primary text-primary-foreground" : "border bg-card text-foreground"}`}>
                  {m.role === "assistant"
                    ? <>
                        <div className="prose-tenx text-sm leading-relaxed [&_li]:my-0.5 [&_ol]:my-1 [&_ol]:pl-5 [&_p]:my-1.5 [&_strong]:font-semibold [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5"><ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown></div>
                      <button onClick={() => draftUpdate(m.content, i)} disabled={drafting === i} className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-brand-ink transition-colors hover:text-foreground" data-testid={`advisor-draft-update-${i}`}>
                          {drafting === i ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />} Turn into draft update
                        </button>
                      </>
                    : <span className="whitespace-pre-wrap">{m.content}</span>}
                </div>
              </div>
            ))
          )}
          {sending && (
            <div className="flex items-center gap-2.5" data-testid="advisor-thinking">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary"><Bot className="h-4 w-4 text-brand" /></div>
              <div className="rounded-2xl border bg-card px-4 py-3"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
            </div>
          )}
        </div>

        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t bg-background pt-3">
          <div className="flex items-end gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask about fundraising, your cap table, investor updates…"
              className="max-h-32 min-h-[44px] resize-none"
              rows={1}
              data-testid="advisor-input"
            />
            <Button type="submit" disabled={sending || !input.trim()} className="h-11 gap-1.5" data-testid="advisor-send">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
          <p className="mt-1.5 text-center text-[11px] text-muted-foreground">Ask TenX gives guidance, not legal or tax advice. Confirm binding decisions with a professional.</p>
        </form>
      </div>
    </AppShell>
  );
}
